/**
 * Test browser lock helpers.
 *
 * Serializes direct Playwright browser usage during Bun test runs so
 * browser-backed unit tests do not compete for local Chromium resources.
 *
 * @module playwright-client/test-browser-lock
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { getCanaryTmpDir } from '@chatsdet/tmp';
import type { BrowserLogger } from '../logger.js';

const TEST_BROWSER_LOCK_DIR = path.join(getCanaryTmpDir(), 'playwright-test-browser-lock');
const TEST_BROWSER_LOCK_METADATA_PATH = path.join(TEST_BROWSER_LOCK_DIR, 'owner.json');
const TEST_BROWSER_LOCK_POLL_MS = 100;
const TEST_BROWSER_LOCK_TIMEOUT_MS = 120_000;
const TEST_BROWSER_LOCK_STALE_MS = 15 * 60_000;

type TestBrowserLockMetadata = {
  pid: number;
  acquiredAt: string;
};

export function shouldUseTestBrowserLock(options: {
  browserLease: boolean;
  cdpUrl?: string;
  browserMode: string;
}): boolean {
  return (
    process.env.NODE_ENV === 'test' &&
    !options.browserLease &&
    !options.cdpUrl &&
    options.browserMode !== 'headed'
  );
}

export async function acquireTestBrowserLock(logger: BrowserLogger): Promise<() => Promise<void>> {
  const startedAt = Date.now();
  const owner: TestBrowserLockMetadata = {
    pid: process.pid,
    acquiredAt: new Date().toISOString(),
  };

  while (true) {
    try {
      await fs.mkdir(TEST_BROWSER_LOCK_DIR);
      await fs.writeFile(TEST_BROWSER_LOCK_METADATA_PATH, JSON.stringify(owner), 'utf8');
      logger.debug('[DirectPlaywright] Acquired test browser lock', {
        lockDir: TEST_BROWSER_LOCK_DIR,
        pid: process.pid,
      });

      let released = false;
      return async () => {
        if (released) return;
        released = true;
        try {
          await fs.rm(TEST_BROWSER_LOCK_DIR, { recursive: true, force: true });
          logger.debug('[DirectPlaywright] Released test browser lock', {
            lockDir: TEST_BROWSER_LOCK_DIR,
            pid: process.pid,
          });
        } catch (error) {
          logger.warn('[DirectPlaywright] Failed to release test browser lock', {
            lockDir: TEST_BROWSER_LOCK_DIR,
            pid: process.pid,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      };
    } catch (error) {
      const code = (error as NodeJS.ErrnoException).code;
      if (code !== 'EEXIST') {
        throw error;
      }

      await cleanupStaleTestBrowserLock(logger);

      if (Date.now() - startedAt >= TEST_BROWSER_LOCK_TIMEOUT_MS) {
        throw new Error(
          `Timed out acquiring test browser lock after ${TEST_BROWSER_LOCK_TIMEOUT_MS}ms`
        );
      }

      await delay(TEST_BROWSER_LOCK_POLL_MS);
    }
  }
}

async function cleanupStaleTestBrowserLock(logger: BrowserLogger): Promise<void> {
  const metadata = await readLockMetadata();
  if (!metadata) {
    const orphanedLockStats = await fs.stat(TEST_BROWSER_LOCK_DIR).catch(() => null);
    if (!orphanedLockStats) {
      return;
    }

    const orphanedAgeMs = Date.now() - orphanedLockStats.mtimeMs;
    if (orphanedAgeMs < 1_000) {
      return;
    }

    logger.warn('[DirectPlaywright] Removing orphaned test browser lock', {
      lockDir: TEST_BROWSER_LOCK_DIR,
      ageMs: orphanedAgeMs,
    });
    await fs.rm(TEST_BROWSER_LOCK_DIR, { recursive: true, force: true });
    return;
  }

  const ageMs = Date.now() - Date.parse(metadata.acquiredAt);
  if (ageMs < TEST_BROWSER_LOCK_STALE_MS && isProcessAlive(metadata.pid)) {
    return;
  }

  logger.warn('[DirectPlaywright] Removing stale test browser lock', {
    lockDir: TEST_BROWSER_LOCK_DIR,
    ownerPid: metadata.pid,
    ageMs,
  });
  await fs.rm(TEST_BROWSER_LOCK_DIR, { recursive: true, force: true });
}

async function readLockMetadata(): Promise<TestBrowserLockMetadata | null> {
  try {
    const raw = await fs.readFile(TEST_BROWSER_LOCK_METADATA_PATH, 'utf8');
    return JSON.parse(raw) as TestBrowserLockMetadata;
  } catch {
    return null;
  }
}

function isProcessAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    return code !== 'ESRCH';
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
